import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  TrendingUp,
  ShieldCheck,
  Zap,
  Building2,
} from "lucide-react";
import Button from "../ui/Button";

const Hero = () => {
  return (
    <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600 text-white overflow-hidden pt-40">
      {/* Background Pattern - Structural Grid */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M0 0h40v40H0V0zm40 40h40v40H40V40z' fill-opacity='0.1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Geometric Pattern Overlay */}
      <div className="absolute right-0 top-0 w-1/2 h-full opacity-10">
        <svg viewBox="0 0 500 500" className="w-full h-full">
          <polygon
            points="250,60 460,400 40,400"
            fill="none"
            stroke="#d4a574"
            strokeWidth="2"
          />
          <polygon
            points="250,100 420,380 80,380"
            fill="none"
            stroke="#d4a574"
            strokeWidth="2"
          />
          <polygon
            points="250,140 380,360 120,360"
            fill="none"
            stroke="#d4a574"
            strokeWidth="2"
          />
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-15">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8 animate-fade-in">
            {/* Brand Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
              <Building2 className="w-5 h-5" />
              <span className="text-sm font-bold tracking-wide">lohamart</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold leading-tight">
              <span className="block text-white">YOUR</span>
              <span className="block text-white">SOURCING</span>
              <span className="block bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent">
                PARTNER
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-100 leading-relaxed font-medium">
              We Deal In All Types Of Steel, Structures And Construction
              Chemicals
            </p>

            <p className="text-lg text-gray-200 leading-relaxed">
              Based in Gujarat, we leverage our decade-long industry know-how, a
              pan-India logistics network, and strong vendor relationships to be
              more than a supplier—we're your strategic materials partner.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/products">
                <Button
                  variant="secondary"
                  size="lg"
                  rightIcon={<ArrowRight className="w-5 h-5" />}
                  className="w-full sm:w-auto"
                >
                  Browse Products
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10"
                >
                  Request Quote
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-6 pt-4 border-t border-white/20">
              <div className="flex items-center space-x-2">
                <ShieldCheck className="w-5 h-5 text-green-400" />
                <span className="text-sm font-medium">Quality Assured</span>
              </div>
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <span className="text-sm font-medium">Best Prices</span>
              </div>
              <div className="flex items-center space-x-2">
                <Zap className="w-5 h-5 text-green-400" />
                <span className="text-sm font-medium">On-Time Delivery</span>
              </div>
            </div>

            {/* Experience Badge */}
            <div className="inline-block bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg px-6 py-4">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-secondary-400">
                  10+
                </span>
                <span className="text-sm text-gray-200">
                  Years of Collective Experience
                </span>
              </div>
            </div>
          </div>

          {/* Right Content - Hero Image */}
          <div className="relative animate-slide-up hidden lg:block">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=800"
                alt="Construction site with steel structures"
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

              {/* Overlay Text on Image */}
              <div className="absolute bottom-8 left-8 right-8">
                <p className="text-white text-lg font-semibold leading-relaxed">
                  Pan-India Supply | Reliable Sourcing | On-Time Delivery
                </p>
              </div>
            </div>

            {/* Floating Card - Quality */}
            <div className="absolute -bottom-6 -left-6 bg-white text-gray-900 p-5 rounded-xl shadow-2xl animate-scale-up">
              <div className="flex items-center space-x-3">
                <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center">
                  <ShieldCheck className="w-7 h-7 text-green-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-600 font-medium">
                    Quality
                  </div>
                  <div className="text-xl font-bold">100% Certified</div>
                </div>
              </div>
            </div>

            {/* Floating Card - Pan India */}
            <div className="absolute -top-6 -right-6 bg-primary-600 text-white p-5 rounded-xl shadow-2xl animate-scale-up">
              <div className="text-center">
                <div className="text-3xl font-bold">26+</div>
                <div className="text-sm font-medium">States Coverage</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave Divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 120"
          className="w-full h-auto"
        >
          <path
            fill="#ffffff"
            d="M0,32L48,37.3C96,43,192,53,288,58.7C384,64,480,64,576,58.7C672,53,768,43,864,48C960,53,1056,75,1152,80C1248,85,1344,75,1392,69.3L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
          />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
